import { parse, stringify as stringifyQS } from 'qs'
import { useCallback, useLayoutEffect, useRef } from 'react'
import {
  formatFilterQueryString,
  formatSortQueryString,
  formatValue,
} from 'components/Commons/PaginatedList/Provider/searchPersistence'

import { useSearchParams } from 'react-router-dom'

const PRIMITIVE_KEYWORDS = {
  true: true,
  false: false,
  null: null,
  undefined: undefined,
}

// primitive values (number, boolean, null, etc) are not automatically parsed with qs,
// a custom decoder is the preferred solution here https://github.com/ljharb/qs/issues/91#issuecomment-437926409
export function primitiveDecoder(value, defaultDecoder, charset) {
  const strWithoutPlus = value.replace(/\+/g, ' ')

  if (charset === 'iso-8859-1') {
    // unescape never throws, no try...catch needed:
    return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape)
  }

  if (/^(\d+|\d*\.\d+)$/.test(value)) {
    return parseFloat(value)
  }

  if (value in PRIMITIVE_KEYWORDS) {
    return PRIMITIVE_KEYWORDS[value]
  }

  // utf-8
  try {
    return decodeURIComponent(strWithoutPlus)
  } catch (e) {
    return defaultDecoder(value)
  }
}

/**
 * Silently (without reload) persist in query string an object of { key: value }
 */
export function useSilentPersistQueryString() {
  const { get, stringify } = useQueryString()
  const [, setSearchParams] = useSearchParams()

  // Filters must land on the URL the user is on right now. Each setSearchParams stays tied to the
  // URL it was created on, so keeping the first one would send every filter change back to the URL
  // this list originally loaded with
  //  e.g. reopening a route modal the user had just closed
  const setSearchParamsRef = useRef(setSearchParams)
  useLayoutEffect(() => {
    setSearchParamsRef.current = setSearchParams
  })

  const silentPersistQueryString = useCallback((object, stringifyOption) => {
    const search = get()

    for (const key in object) {
      const value = object[key]
      if (value !== undefined) {
        search[key] = value
      } else {
        delete search[key]
      }
    }

    const stringifiedQs = stringify(search, stringifyOption)

    setSearchParamsRef.current(stringifiedQs, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return silentPersistQueryString
}

function useQueryString() {
  const get = useCallback(parseOptions => {
    const { search } = new URL(window.location)
    return (
      parse(search, {
        ignoreQueryPrefix: true,
        ...parseOptions,
      }) || {}
    )
  }, [])

  const set = useCallback(({ current, key, value }) => {
    let search = current
    if (value !== undefined) {
      search[key] = value
    } else {
      delete search[key]
    }
    return search
  }, [])

  const stringify = useCallback((search, stringifyOption) => {
    return Object.keys(search).length > 0 ? `?${stringifyQS(search, stringifyOption)}` : undefined
  }, [])

  return { get, set, stringify }
}

function filterStringifyValues(_, value) {
  return formatValue(value)
}

export function useLinkQueryString(stringifyOptions) {
  const qs = useQueryString()

  function getLinkQueryString({ pathname, items, clearQs = false }) {
    const current = clearQs ? {} : qs.get()
    let updated = current
    for (const { key, value } of items) {
      updated = qs.set({ current: updated, key, value })
    }

    const search = qs.stringify(updated, stringifyOptions)

    return { pathname, search }
  }

  return getLinkQueryString
}

export function useLinkFilterQueryString() {
  const getLinkQueryString = useLinkQueryString({ filter: filterStringifyValues })
  function getLinkFilterQueryString({ pathname, filters = [], sort = [], clearQs = false }) {
    const formattedFilters = filters.map(({ key, value }) => formatFilterQueryString({ key, value }))
    const formattedSort = sort.map(({ key, value }) => formatSortQueryString({ key, value }))
    return getLinkQueryString({ pathname, items: [...formattedFilters, ...formattedSort], clearQs })
  }
  return getLinkFilterQueryString
}

export default useQueryString
